import { useMemo } from 'react';
import useApi from './useApi';
import { uploadBase64, uploadFile } from '@src/utils/upload';
import { getStandardizedFileName } from '@src/utils/upload/filenameUtils';

export const apiConfig = {
  studioBulletDefaults: '/api/app/settings/studioBulletDefaults',
  story: '/api/app/story',
  clip: '/api/app/clips',
  studioStory: '/api/app/studioStory',
  studioStoryThumbnail: '/api/app/studioStoryThumbnail',
  studioStoryIcon: '/api/app/studioStoryIcon',
  studioContentBank: 'api/app/contentBankAssetDefinition',
  studioCardsAsset: 'api/app/studioCards',
  template: '/api/app/template',
  templateFolders: '/api/app/template-folders',
  studioTemplate: '/api/app/studioTemplate',
  media: '/api/app/media',
  auth: '/api/auth/refresh',
  userInfo: '/api/app/user/userInfo',
  mediaProvider: '/api/app/mediaProvider',
  mediaSource: '/api/app/mediaSource',
  pageVisibility: (pageId) => `api/app/page/${pageId}/toggleHidden`,
  externalAssets: '/api/app/externalAssets',
  feature: '/api/app/feature',
  mediaCollections: (id) => `/api/app/mediaProvider/${id}/collections`,
  studioFont: '/api/app/studioFont',
  studioShapes: '/api/app/studioShape',
  studioStickers: '/api/app/studioSticker',
  studioTextSets: '/api/app/studioTextSet',
  studioClip: '/api/app/studioClip',
  studioClipThumbnail: '/api/app/studioClipThumbnail',
  studioAd: '/api/app/studioAd',
  studioSuggestions: '/api/app/studioSuggestions',
  studioSavedColors: '/api/app/studioSavedColors',
  studioAnimation: '/api/app/studioAnimation',
  studioLutFilters: '/api/app/studio/lut-filters',
  animatedThumbnail: (resource, id, action, shape) => {
    const path = `/api/app/${encodeURIComponent(
      resource ?? ''
    )}/${encodeURIComponent(id ?? '')}/animated-thumbnail${
      action ? `/${encodeURIComponent(action)}` : ''
    }`;
    return shape ? `${path}?shape=${encodeURIComponent(shape)}` : path;
  },
  r2: {
    presignedUrls: '/api/app/uploads/presigned-url/r2',
  },
  r2Multipart: {
    initiate: '/api/app/uploads/r2/multipart/initiate',
    presignedUrls: '/api/app/uploads/r2/multipart/presigned-urls',
    complete: '/api/app/uploads/r2/multipart/complete',
    abort: (uploadId, objectKey) =>
      `/api/app/uploads/r2/multipart/abort/${uploadId}/${objectKey}`,
  },
};

export const createCmsApi = (api) => {
  const encodeQueryValue = (value) => encodeURIComponent(value ?? '');

  const voloDefaultApiClient = (url) => {
    return {
      getList: (params, signal) =>
        api.get(url, { params, signal }).then((res) => {
          return { payload: res.data };
        }),
    };
  };

  const uploadFileToR2 = async (correlationId, data, file, updateProgress) => {
    const response = await uploadFile({
      correlationId,
      file,
      updateProgress,
      r2UploadsApi,
    });
    return response.assetUrl;
  };

  const uploadBase64ToR2 = async (correlationId, resource, updateProgress) => {
    const standardizedFileName = getStandardizedFileName(
      resource.fileName || 'original'
    );

    const response = await uploadBase64({
      correlationId,
      base64Str: resource.base64Str,
      contentType: resource.mimeType,
      fileName: standardizedFileName,
      updateProgress,
      r2UploadsApi,
    });
    return response.assetUrl;
  };

  const storyApi = {
    get: (storyId) =>
      api.get(`${apiConfig.story}/${storyId}`).then((res) => {
        return { payload: res.data };
      }),
  };

  const clipApi = {
    get: (clipId) =>
      api.get(`${apiConfig.clip}/${clipId}`).then((res) => {
        return { payload: res.data };
      }),
  };

  const animatedThumbnailApi = {
    get: (resource, id, shape) =>
      api
        .get(apiConfig.animatedThumbnail(resource, id, null, shape))
        .then((res) => {
          return { payload: res.data };
        }),
    generate: (resource, id, startSeconds, sourceId, shape, composition) =>
      api
        .post(apiConfig.animatedThumbnail(resource, id, 'generate', shape), {
          startSeconds,
          ...(sourceId ? { sourceId } : {}),
          ...(composition || {}),
        })
        .then((res) => {
          return { payload: res.data };
        }),
  };

  const studioStoryApi = {
    get: (id, storyId) =>
      api.get(`${apiConfig.studioStory}/${id}/${storyId}`).then((res) => {
        return { payload: res.data };
      }),
    save: (page) => api.post(`${apiConfig.studioStory}/save`, page),
    generatePages: (request) =>
      api.post(`${apiConfig.studioStory}/generatepages`, request),
    togglePageHidden: (pageId) =>
      api.post(`${apiConfig.pageVisibility(pageId)}`, { pageId }),
  };

  const studioStoryThumbnailApi = {
    get: (id, storyId) =>
      api
        .get(`${apiConfig.studioStoryThumbnail}/${id}/${storyId}`)
        .then((res) => {
          return { payload: res.data };
        }),
    render: (request) =>
      api.post(`${apiConfig.studioStoryThumbnail}/render`, request),
  };

  const studioStoryIconApi = {
    get: (id, storyId) =>
      api.get(`${apiConfig.studioStoryIcon}/${id}/${storyId}`).then((res) => {
        return { payload: res.data };
      }),
    render: (request) =>
      api.post(`${apiConfig.studioStoryIcon}/render`, request),
  };

  const studioContentBankApi = {
    get: (id, contentBankAssetId) =>
      api
        .get(`${apiConfig.studioContentBank}/${id}/${contentBankAssetId}`)
        .then((res) => {
          return { payload: res.data };
        }),
    render: (request) =>
      api.post(`${apiConfig.studioContentBank}/render`, request),
  };

  const studioCardsAssetApi = {
    get: (id, cardsAssetId) =>
      api
        .get(`${apiConfig.studioCardsAsset}/${id}/${cardsAssetId}`)
        .then((res) => {
          return { payload: res.data };
        }),
    render: (request) =>
      api.post(`${apiConfig.studioCardsAsset}/render`, request),
  };

  const studioClipApi = {
    get: (id, clipId) =>
      api.get(`${apiConfig.studioClip}/${id}/${clipId}`).then((res) => {
        return { payload: res.data };
      }),
    render: (request) => api.post(`${apiConfig.studioClip}/render`, request),
  };

  const studioClipThumbnailApi = {
    get: (id, clipId) =>
      api
        .get(`${apiConfig.studioClipThumbnail}/${id}/${clipId}`)
        .then((res) => {
          return { payload: res.data };
        }),
    render: (request) =>
      api.post(`${apiConfig.studioClipThumbnail}/render`, request),
  };

  const studioAdApi = {
    get: (id, adId) =>
      api.get(`${apiConfig.studioAd}/${id}/${adId}`).then((res) => {
        return { payload: res.data };
      }),
    render: (request) => api.post(`${apiConfig.studioAd}/render`, request),
  };

  const studioSuggestionsApi = {
    generateSuggestions: (request) =>
      api.post(apiConfig.studioSuggestions, request).then((res) => {
        return { payload: res.data.suggestions || [] };
      }),
  };

  const studioSavedColorsApi = {
    getList: (params, signal) =>
      api
        .get(apiConfig.studioSavedColors, { params, signal })
        .then((res) => ({ payload: res.data })),
    addSavedColor: (request) =>
      api.put(apiConfig.studioSavedColors, request).then((res) => {
        return { payload: res.data.color };
      }),
    editSavedColor: (request) =>
      api
        .patch(`${apiConfig.studioSavedColors}/${request.id}`, request)
        .then((res) => {
          return { payload: res.data.color };
        }),
    // deleteSavedColor: (request) =>
    //   api
    //     .delete(`${apiConfig.studioSavedColors}/${request.id}`, request)
    //     .then((res) => {
    //       return { payload: null };
    //     }),
  };

  const studioAnimationApi = {
    getList: (params, signal) =>
      api
        .get(apiConfig.studioAnimation, {
          params,
          signal,
        })
        .then((res) => ({ payload: res.data })),
    get: (id) =>
      api.get(`${apiConfig.studioAnimation}/${id}`).then((res) => {
        return { payload: res.data };
      }),
  };

  const studioLutFiltersApi = {
    getList: (params, signal) =>
      api
        .get(apiConfig.studioLutFilters, { params, signal })
        .then((res) => ({ payload: res.data })),
    resolveVersions: (versionIds, signal) =>
      api
        .post(
          `${apiConfig.studioLutFilters}/versions/resolve`,
          { versionIds },
          { signal }
        )
        .then((res) => ({ payload: res.data })),
  };

  const studioTemplateApi = {
    get: (id, templateId) =>
      api.get(`${apiConfig.studioTemplate}/${id}/${templateId}`).then((res) => {
        return { payload: res.data };
      }),
    save: (request) => api.post(`${apiConfig.studioTemplate}/save`, request),
  };

  const templateApi = {
    _delete: (id) => api.delete(`${apiConfig.template}/${id}`),
    deleteStudioTemplate: (id, assetType) =>
      api.delete(
        `${apiConfig.template}/${id}/studio?assetType=${encodeQueryValue(
          assetType
        )}`
      ),
    save: (template) =>
      api.post(`${apiConfig.template}/save`, template).then((res) => {
        return { payload: res.data };
      }),
    updateStudioMetadata: (id, request) =>
      api
        .put(`${apiConfig.template}/${id}/studio-metadata`, request)
        .then((res) => {
          return { payload: res.data };
        }),
    updateStudioOrder: (id, request) =>
      api
        .put(`${apiConfig.template}/${id}/studio-order`, request)
        .then((res) => {
          return { payload: res.data };
        }),
    updateStudioPin: (id, request) =>
      api.put(`${apiConfig.template}/${id}/studio-pin`, request).then((res) => {
        return { payload: res.data };
      }),
    moveStudioTemplate: (id, request) =>
      api
        .put(`${apiConfig.template}/${id}/studio-move`, request)
        .then((res) => ({ payload: res.data })),
    getList: (
      pageNumber,
      pageSize = 16,
      folderName = '',
      searchText = '',
      signal,
      assetType = null,
      folderId = null,
      includeDescendants = false
    ) => {
      let getListUrl = `${
        apiConfig.template
      }/getListForStudio?pageNumber=${pageNumber}&pageSize=${pageSize}&searchText=${encodeQueryValue(
        searchText
      )}`;
      if (folderName) {
        getListUrl += `&folderName=${encodeQueryValue(folderName)}`;
      }
      if (folderId) {
        getListUrl += `&folderId=${encodeQueryValue(folderId)}`;
      }
      if (includeDescendants) {
        getListUrl += '&includeDescendants=true';
      }
      if (assetType) {
        getListUrl += `&assetType=${encodeQueryValue(assetType)}`;
      }
      const request = signal
        ? api.get(getListUrl, { signal })
        : api.get(getListUrl);
      return request.then((res) => {
        return { payload: res.data || [] };
      });
    },
    getFolders: (
      pageNumber,
      pageSize = 16,
      searchText = '',
      assetType = null
    ) => {
      let getFoldersUrl = `${
        apiConfig.template
      }/templateFolders?pageNumber=${pageNumber}&pageSize=${pageSize}&searchText=${encodeQueryValue(
        searchText
      )}`;

      if (assetType) {
        getFoldersUrl += `&assetType=${encodeQueryValue(assetType)}`;
      }
      return api.get(getFoldersUrl).then((res) => {
        return { payload: res.data || [] };
      });
    },
  };

  const templateFolderApi = {
    getList: (params, signal) =>
      api
        .get(apiConfig.templateFolders, { params, signal })
        .then((res) => ({ payload: res.data })),
    get: (id, signal) =>
      api
        .get(`${apiConfig.templateFolders}/${encodeQueryValue(id)}`, { signal })
        .then((res) => ({ payload: res.data })),
    getDestinations: (params, signal) =>
      api
        .get(`${apiConfig.templateFolders}/destinations`, { params, signal })
        .then((res) => ({ payload: res.data })),
  };

  const mediaApi = {
    upload: (media) =>
      api.post(`${apiConfig.media}/upload`, media).then((res) => {
        return { payload: res.data };
      }),
    addPoster: (poster) =>
      api.post(`${apiConfig.media}/poster`, poster).then((res) => {
        return { payload: res.data };
      }),
    _delete: (id) => api.delete(`${apiConfig.media}/${id}`),
    get: (search) =>
      api.get(`${apiConfig.media}`, { params: search }).then((res) => {
        return { payload: res.data };
      }),
  };

  const userApi = {
    get: () =>
      api.get(`${apiConfig.userInfo}`).then((res) => {
        return { payload: res.data };
      }),
  };

  const mediaProviderApi = {
    getList: () =>
      api.get(`${apiConfig.mediaProvider}`).then((res) => {
        return { payload: res.data };
      }),
    getMediaDetails: (mediaProviderId, assetId, signal) =>
      api
        .get(
          `${apiConfig.mediaProvider}/${encodeURIComponent(
            mediaProviderId
          )}/media/${encodeURIComponent(assetId)}`,
          { signal }
        )
        .then((res) => res.data),
    getMedias: (mediaProviderId, params) =>
      api
        .get(`${apiConfig.mediaProvider}/${mediaProviderId}/media`, { params })
        .then((res) => {
          return { payload: res.data };
        }),
    registerUsage: (mediaProviderId, { registerUsageUrl }) =>
      api.post(
        `${apiConfig.mediaProvider}/${mediaProviderId}/registerUsage${registerUsageUrl}`
      ),
    download: (mediaProviderId, { assetId }) =>
      api
        .post(
          `${apiConfig.mediaProvider}/${mediaProviderId}/download/${assetId}`
        )
        .then((res) => {
          return { url: res.data };
        }),
  };

  const externalAssetsApi = {
    getExternalAssets: (params, signal) =>
      api
        .get(`${apiConfig.externalAssets}`, {
          params,
          signal,
        })
        .then((res) => {
          return { payload: res.data };
        }),
  };

  const featureApi = {
    getFeatures: () =>
      //TODO: leave it for now, it will just return features for current tenant
      api
        .get(`${apiConfig.feature}/00000000-0000-0000-0000-000000000000`)
        .then((res) => {
          return { payload: res.data };
        }),
  };

  const mediaSourceApi = {
    _delete: (id) => api.delete(`${apiConfig.mediaSource}/externalAsset/${id}`),
  };

  const mediaCollectionsApi = {
    getList: (mediaProviderId, searchTerm, folder) =>
      api
        .get(apiConfig.mediaCollections(mediaProviderId), {
          params: { searchTerm, folder },
        })
        .then((res) => {
          return { payload: res.data };
        }),
  };

  const fontsApi = voloDefaultApiClient(apiConfig.studioFont);

  const shapesApi = voloDefaultApiClient(apiConfig.studioShapes);

  const stickersApi = voloDefaultApiClient(apiConfig.studioStickers);

  const textSetsApi = voloDefaultApiClient(apiConfig.studioTextSets);

  const r2UploadsApi = {
    getPresignedUrl: (request) =>
      api.post(apiConfig.r2.presignedUrls, request).then((res) => {
        return { payload: res.data };
      }),
    initiateMultipart: (request) =>
      api.post(apiConfig.r2Multipart.initiate, request).then((res) => {
        return { payload: res.data };
      }),
    getMultipartPresignedUrls: (request) =>
      api.post(apiConfig.r2Multipart.presignedUrls, request).then((res) => {
        return { payload: res.data };
      }),
    completeMultipart: (request) =>
      api.post(apiConfig.r2Multipart.complete, request).then((res) => {
        return { payload: res.data };
      }),
    abortMultipart: (uploadId, objectKey) =>
      api
        .delete(apiConfig.r2Multipart.abort(uploadId, objectKey))
        .then((res) => {
          return { payload: res.data };
        }),
  };

  return {
    studioBulletDefaultsApi: {
      get: (signal) =>
        api
          .get(apiConfig.studioBulletDefaults, { signal, timeout: 10000 })
          .then((response) => response?.data || null),
    },
    storyApi,
    clipApi,
    animatedThumbnailApi,
    studioStoryApi,
    studioStoryThumbnailApi,
    studioStoryIconApi,
    studioContentBankApi,
    studioCardsAssetApi,
    studioTemplateApi,
    templateApi,
    templateFolderApi,
    mediaApi,
    uploadsApi: {
      uploadFileToR2,
      uploadBase64ToR2,
    },
    r2UploadsApi,
    userApi,
    mediaProviderApi,
    mediaSourceApi,
    mediaCollectionsApi,
    featureApi,
    fontsApi,
    externalAssetsApi,
    shapesApi,
    stickersApi,
    textSetsApi,
    studioClipApi,
    studioClipThumbnailApi,
    studioAdApi,
    studioSuggestionsApi,
    studioSavedColorsApi,
    studioAnimationApi,
    studioLutFiltersApi,
  };
};

const useCmsApi = () => {
  const api = useApi();

  const cmsApi = useMemo(() => createCmsApi(api), [api]);

  return cmsApi;
};

export default useCmsApi;
